import { useNavigate } from "react-router-dom";
import { useClaimState } from "../state";

type ServiceRow = {
  id: string;
  type: "wrench";
  status: "red" | "check" | "gray" | "green" | "none";
  service: string;
  ref: string;
  assignedTo: string;
  nextAction: string;
  nextActionLink?: boolean;
  actionOwner: string;
  relatedTo: string;
  services: string;
  vendor: string;
  vendorLink: string;
  target: string;
  quote?: string;
};

const baseRows: ServiceRow[] = [
  {
    id: "207",
    type: "wrench",
    status: "red",
    service: "207",
    ref: "5a497f19-ad0b-4bdd-a467-fcba841cc176",
    assignedTo: "Super User",
    nextAction: "Follow up with vendor - work past due",
    actionOwner: "Super User",
    relatedTo: "Claim",
    services: "Voltaire",
    vendor: "Voltaire",
    vendorLink: "Voltaire",
    target: "03/19/2026",
  },
  {
    id: "301",
    type: "wrench",
    status: "check",
    service: "301",
    ref: "1d2ab0b2-bc97-448c-9004-2d1a801fed12",
    assignedTo: "Super User",
    nextAction: "Add invoice",
    nextActionLink: true,
    actionOwner: "Voltaire",
    relatedTo: "Claim",
    services: "Voltaire",
    vendor: "Voltaire",
    vendorLink: "Voltaire",
    target: "",
  },
  {
    id: "303",
    type: "wrench",
    status: "gray",
    service: "303",
    ref: "59ca96ce-f2cf-4a72-864e-a0be9c63a4a8",
    assignedTo: "Super User",
    nextAction: "None - request canceled",
    actionOwner: "",
    relatedTo: "Claim",
    services: "Voltaire",
    vendor: "Voltaire",
    vendorLink: "Voltaire",
    target: "",
  },
  {
    id: "401",
    type: "wrench",
    status: "none",
    service: "401",
    ref: "c068b073-e89b-4215-ad4f-5f6dbb366056",
    assignedTo: "Super User",
    nextAction: "Finish the work",
    nextActionLink: true,
    actionOwner: "Voltaire",
    relatedTo: "Claim",
    services: "Voltaire",
    vendor: "Voltaire",
    vendorLink: "Voltaire",
    target: "03/21/2026",
  },
];

export function ServicesPage() {
  const navigate = useNavigate();
  const { serviceCompleted } = useClaimState();

  const finalRow: ServiceRow = serviceCompleted
    ? {
        id: "403",
        type: "wrench",
        status: "check",
        service: "403",
        ref: "8ddf087f-3401-4bfe-b8a4-d29743cddea9",
        assignedTo: "Super User",
        nextAction: "Add invoice",
        nextActionLink: true,
        actionOwner: "Voltaire",
        relatedTo: "Claim",
        services: "Voltaire",
        vendor: "Voltaire",
        vendorLink: "Voltaire",
        target: "",
      }
    : {
        id: "403",
        type: "wrench",
        status: "green",
        service: "403",
        ref: "",
        assignedTo: "",
        nextAction: "Go to Voltaire System",
        nextActionLink: true,
        actionOwner: "Voltaire",
        relatedTo: "Claim",
        services: "Voltaire",
        vendor: "Voltaire",
        vendorLink: "Voltaire",
        target: "03/27/2026",
      };

  const rows: ServiceRow[] = [...baseRows, finalRow];

  function renderStatus(status: ServiceRow["status"]) {
    if (status === "red") {
      return (
        <span className="status-icon red" aria-label="error">
          !
        </span>
      );
    }
    if (status === "check") {
      return <span className="status-icon check-bare">✓</span>;
    }
    if (status === "gray") {
      return <span className="status-icon gray" />;
    }
    if (status === "green") {
      return (
        <span className="status-icon green" aria-label="completed">
          ✓
        </span>
      );
    }
    return null;
  }

  return (
    <div>
      <div className="page-title">
        <h1>Services</h1>
        <div className="top-right-actions">
          <button className="btn btn-primary">New Service</button>
        </div>
      </div>
      <div className="services-table-wrapper">
        <table className="services">
          <thead>
            <tr>
              <th>
                Type<span className="sort-arrows">⇅</span>
              </th>
              <th>
                Status<span className="sort-arrows">⇅</span>
              </th>
              <th>
                Service #<span className="sort-arrows">⇅</span>
              </th>
              <th>
                Ref #<span className="sort-arrows">⇅</span>
              </th>
              <th>
                Assigned To<span className="sort-arrows">⇅</span>
              </th>
              <th>Next Action</th>
              <th>
                Action Owner<span className="sort-arrows">⇅</span>
              </th>
              <th>
                Related To<span className="sort-arrows">⇅</span>
              </th>
              <th>
                Services<span className="sort-arrows">⇅</span>
              </th>
              <th>
                Vendor<span className="sort-arrows">⇅</span>
              </th>
              <th>
                Target<span className="sort-arrows">⇅</span>
              </th>
              <th>
                Quote<span className="sort-arrows">⇅</span>
              </th>
              <th style={{ width: 24 }}>⊞</th>
            </tr>
          </thead>
          <tbody>
            <tr className="related-row">
              <td colSpan={13}>
                <span className="related-collapse">▴</span>
                Related To: Claim
              </td>
            </tr>
            {rows.map((row, idx) => {
              const isSelected = row.id === "403";
              return (
                <tr key={row.id} className={isSelected ? "selected" : ""}>
                  <td>
                    <span className="wrench-icon">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M.102 2.223A3.004 3.004 0 0 0 3.78 5.897l6.341 6.252A3.003 3.003 0 0 0 13 16a3 3 0 1 0-.851-5.878L5.897 3.781A3.004 3.004 0 0 0 2.223.1l2.141 2.142L4 4l-1.757.364L.102 2.223z" />
                      </svg>
                    </span>
                  </td>
                  <td>{renderStatus(row.status)}</td>
                  <td>{row.service}</td>
                  <td>
                    <span className="uuid">{row.ref}</span>
                  </td>
                  <td>{row.assignedTo}</td>
                  <td>
                    {row.nextActionLink ? (
                      <a href="#">{row.nextAction}</a>
                    ) : (
                      row.nextAction
                    )}
                  </td>
                  <td>{row.actionOwner}</td>
                  <td>{row.relatedTo}</td>
                  <td>{row.services}</td>
                  <td>
                    <a href="#">{row.vendorLink}</a>
                  </td>
                  <td>{row.target}</td>
                  <td>{row.quote || ""}</td>
                  <td />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="tabbed-panel">
        <div className="tabs">
          <div className="tab active">Details</div>
          <div className="tab">History</div>
          <div className="tab">Activities</div>
          <div className="tab">Documents</div>
          <div className="tab">Notes</div>
          <div className="tab">Invoices</div>
          <div className="tab">Messages</div>
        </div>
        <div className="action-bar">
          {serviceCompleted ? (
            <>
              <button className="btn btn-action">Assign</button>
              <button className="btn btn-action btn-with-arrow">Record Vendor Progress</button>
              <button className="btn btn-action-disabled" disabled>
                Go To Voltaire System
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-action">Cancel Service</button>
              <button className="btn btn-action">Assign</button>
              <button className="btn btn-action btn-with-arrow">Record Vendor Progress</button>
              <button
                className="btn btn-action"
                onClick={() => navigate("/voltaire")}
              >
                Go To Voltaire System
              </button>
            </>
          )}
        </div>

        <div className="detail-grid">
          <div className="detail-left">
            <div className="detail-row">
              <span className="detail-label">Service Number</span>
              <span className="detail-value">403</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Reference Number</span>
              <span className="detail-value">
                {serviceCompleted ? "8ddf087f-3401-4bfe-b8a4-d29743cddea9" : ""}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Progress</span>
              <span className="detail-value">
                {serviceCompleted ? "Work Complete" : "Requested"}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">External Status</span>
              <span className="detail-value">
                {serviceCompleted ? "Completed" : "In Progress"}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">External Next Step</span>
              <span className="detail-value" />
            </div>
            <div className="detail-row">
              <span className="detail-label">Next Action</span>
              <span className="detail-value">
                {serviceCompleted ? "Add invoice" : "Go to Voltaire System"}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Action Owner</span>
              <span className="detail-value">Voltaire</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Related To</span>
              <span className="detail-value">Claim</span>
            </div>
            {serviceCompleted ? (
              <div className="detail-row">
                <span className="detail-label">Completion Date</span>
                <span className="detail-value">03/20/2026</span>
              </div>
            ) : (
              <>
                <div className="detail-row">
                  <span className="detail-label">Requested Service Completion Date</span>
                  <span className="detail-value">03/27/2026</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Expected Service Completion Date</span>
                  <span className="detail-value">03/27/2026</span>
                </div>
              </>
            )}
            <div className="detail-row">
              <span className="detail-label">Currency</span>
              <span className="detail-value">USD</span>
            </div>
          </div>
          <div className="detail-right">
            <div className="invoices-panel">
              <h3>Invoices</h3>
              {serviceCompleted && (
                <div style={{ marginBottom: 14 }}>
                  <button className="btn btn-primary">Add Invoice</button>
                </div>
              )}
              <div className="text-muted">No invoices for this service.</div>
            </div>

            <div className="metrics-card">
              <div className="metrics-header">Metrics</div>
              <table className="metrics">
                <thead>
                  <tr>
                    <th style={{ width: 50 }}>Status</th>
                    <th>Metric</th>
                    <th>Value</th>
                    <th>Target</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <span className="status-icon green">✓</span>
                    </td>
                    <td>Response Time</td>
                    <td>0 hours{serviceCompleted ? "" : "+"}</td>
                    <td />
                  </tr>
                  <tr>
                    <td />
                    <td>Service Timeliness</td>
                    <td>
                      {serviceCompleted ? "Completed on time" : "0 days+"}
                    </td>
                    <td>{serviceCompleted ? "0 days" : "5 days"}</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="status-icon green">✓</span>
                    </td>
                    <td>Number Of Delays</td>
                    <td>0</td>
                    <td>0</td>
                  </tr>
                  <tr>
                    <td />
                    <td>Cycle Time</td>
                    <td>0 days{serviceCompleted ? "" : "+"}</td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
